import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Star, MapPin, Package, ArrowRight } from "lucide-react";
import { formatDate } from "@/lib/api";

const ProviderProfile = () => {
  const { id } = useParams();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const load = async () => {
      const { data } = await api.get(`/providers/${id}`);
      setProvider(data);
      try {
        const { data: r } = await api.get(`/reviews/provider/${data.user_id}`);
        setReviews(r);
      } catch {}
    };
    load();
  }, [id]);

  if (!provider) return (<div><Navbar /><div className="container-page py-20 text-center">Načítání…</div></div>);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-10">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-8">
              <div className="flex items-start gap-5">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-600 grid place-items-center text-white font-display font-bold text-3xl">
                  {provider.business_name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="font-display text-3xl font-bold text-slate-900">{provider.business_name}</h1>
                    {provider.verified && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        ✓ Ověřeno
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-slate-500 flex items-center gap-1"><MapPin className="w-4 h-4" /> {provider.city} {provider.zip}</div>
                  <div className="mt-3 flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                      <span className="font-semibold">{provider.rating_avg || "—"}</span>
                      <span className="text-slate-500">({provider.rating_count})</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-600">
                      <Package className="w-4 h-4" /> {provider.total_orders} zakázek
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-5 text-slate-700 leading-relaxed">{provider.description || "Profesionální prádelna s individuálním přístupem."}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {provider.services?.map((s) => (
                  <span key={s} className="text-xs px-3 py-1 rounded-full bg-sky-100 text-sky-800 font-semibold">
                    {s === "premium" ? "Premium (žehlení)" : "Standard (složení)"}
                  </span>
                ))}
                <span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-700">Kapacita: {provider.capacity_per_week_kg} kg/týden</span>
              </div>
            </Card>

            <Card className="p-7">
              <h2 className="font-display text-xl font-bold mb-4">Hodnocení zákazníků</h2>
              {reviews.length === 0 ? (
                <div className="text-sm text-slate-500">Zatím žádná hodnocení.</div>
              ) : (
                <div className="space-y-5">
                  {reviews.map((r) => (
                    <div key={r.id} className="pb-5 border-b border-slate-100 last:border-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-slate-900">{r.from_user_name}</div>
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`w-4 h-4 ${i < r.overall ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                          ))}
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 mb-2">{formatDate(r.created_at)}</div>
                      {r.comment && <p className="text-sm text-slate-700">"{r.comment}"</p>}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div>
            <Card className="p-6 sticky top-24">
              <div className="tiny-label mb-2">Objednat u této prádelny</div>
              <p className="text-sm text-slate-600 mb-4">Vytvořte objednávku – systém vás propojí s touto prádelnou, pokud bude v okolí.</p>
              <Link to="/registrace?role=customer" className="cta-primary w-full justify-center" data-testid="provider-profile-order">
                Objednat praní <ArrowRight className="w-4 h-4" />
              </Link>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProviderProfile;
